import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ActivityIcon as Activity,
    TrophyIcon as Award,
    CalendarIcon as Calendar,
    MessageIcon as MessageSquare,
    TrendingUpIcon as TrendingUp,
    CheckCircleIcon as CheckCircle2,
    CheckboxIcon as TaskIcon
} from '../components/Icons';
import axios from 'axios';
import AuthContext from '../context/AuthContext';

const PulseCard = ({ icon: Icon, title, value, subtext, color = 'primary', loading = false, onClick }) => {
    const colorClasses = {
        primary: 'bg-primary/10 text-primary',
        secondary: 'bg-secondary/10 text-secondary',
        tertiary: 'bg-tertiary/10 text-tertiary',
        error: 'bg-error/10 text-error',
        success: 'bg-green-500/10 text-green-600'
    };

    if (loading) {
        return (
            <div className="bg-surface-containerLow rounded-2xl p-4 animate-pulse flex flex-col gap-3 min-w-[160px]">
                <div className="w-10 h-10 rounded-full bg-surface-onVariant/10" />
                <div className="h-6 w-24 bg-surface-onVariant/10 rounded" />
                <div className="h-4 w-16 bg-surface-onVariant/10 rounded" />
            </div>
        );
    }

    return (
        <div
            onClick={onClick}
            className={`bg-surface-containerLow rounded-2xl p-5 flex flex-col gap-3 min-w-[160px] 
                  border border-transparent hover:border-surface-outline transition-all duration-300
                  ${onClick ? 'cursor-pointer hover:shadow-elevation-1 hover:-translate-y-1' : ''}`}
        >
            <div className="flex justify-between items-start">
                <div className={`p-2.5 rounded-xl ${colorClasses[color]}`}>
                    <Icon style={{ fontSize: 22 }} />
                </div>
                {value !== undefined && <span className="text-display-small font-bold text-surface-on">{value}</span>}
            </div>
            <div>
                <h4 className="text-label-large font-bold text-surface-on opacity-90">{title}</h4>
                {subtext && <p className="text-body-small text-surface-onVariant mt-1">{subtext}</p>}
            </div>
        </div>
    );
};


const DashboardPulse = ({ profile, API_URL }) => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        upcomingEvent: null,
        pendingContactRequests: 0,
        pendingBlogPosts: 0,
        myOpenTasksCount: 0
    });
    const [loading, setLoading] = useState(true);

    const { user } = useContext(AuthContext);

    useEffect(() => {
        const fetchPulseData = async () => {
            if (!user || !user.token) return;

            try {
                setLoading(true);
                const config = {
                    headers: {
                        Authorization: `Bearer ${user.token}`
                    }
                };
                const { data } = await axios.get(`${API_URL}/pulse`, config);

                setStats({
                    upcomingEvent: data.upcomingEvent,
                    pendingContactRequests: data.pendingContactRequests,
                    pendingBlogPosts: data.pendingBlogPosts,
                    myOpenTasksCount: data.myOpenTasksCount || 0
                });
            } catch (err) {
                console.error("Pulse load failed", err);
            } finally {
                setLoading(false);
            }
        };

        fetchPulseData();
    }, [user, API_URL]);

    const isAdminOrLeader = profile.role === 'admin' || profile.role === 'gruppenleiter';

    return (
        <section className="animate-fade-in-down mb-10">
            <div className="flex items-center gap-2 mb-4">
                <Activity style={{ fontSize: 20 }} className="text-primary" />
                <h2 className="text-title-medium font-bold text-surface-on uppercase tracking-widest text-sm">Pulse</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Universal Card */}
                <PulseCard
                    icon={Calendar}
                    title="Nächstes Event"
                    value={stats.upcomingEvent?.date || 'Keines'}
                    subtext={stats.upcomingEvent?.title || 'Geplant'}
                    color="primary"
                    loading={loading}
                />

                {/* Admin/Leader Stats */}
                {isAdminOrLeader && (
                    <>
                        <PulseCard
                            icon={MessageSquare}
                            title="Kontaktanfragen"
                            value={stats.pendingContactRequests}
                            subtext="Eingegangen"
                            color="secondary"
                            loading={loading}
                        />
                        <PulseCard
                            icon={CheckCircle2}
                            title="Blog-Freigaben"
                            value={stats.pendingBlogPosts}
                            subtext="Wartend"
                            color="error"
                            loading={loading}
                        />
                        <PulseCard
                            icon={TaskIcon}
                            title="Meine Aufgaben"
                            value={stats.myOpenTasksCount}
                            subtext="Offen & mir zugewiesen"
                            color="success"
                            loading={loading}
                            onClick={() => navigate('/tasks')}
                        />
                    </>
                )}
            </div>
        </section>
    );
};

export default DashboardPulse;
